import { useState, useEffect } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, TextField, Alert,
    CircularProgress,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { founderSyndicationApi } from '../../utils/syndicationApi';

export default function FounderProfile() {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [linkedinUrl, setLinkedinUrl] = useState('');
    const [bio, setBio] = useState('');

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            const res = await founderSyndicationApi.get('founder/profile/me/');
            const data = res.data?.data;
            setProfile(data);
            setLinkedinUrl(data?.linkedin_url || '');
            setBio(data?.bio || '');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load profile.');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError('');
        setSuccess('');
        try {
            const res = await founderSyndicationApi.patch('founder/profile/update/', {
                linkedin_url: linkedinUrl,
                bio,
            });
            setProfile(res.data?.data);
            setSuccess('Profile updated successfully.');
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to update profile.');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
    }

    if (!profile) {
        return <Alert severity="error">{error || 'Profile not found.'}</Alert>;
    }

    return (
        <Box>
            <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>My Profile</Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

            <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                    <Card>
                        <CardContent sx={{ textAlign: 'center', py: 4 }}>
                            <Box sx={{
                                width: 80, height: 80, borderRadius: '50%', mx: 'auto', mb: 2,
                                background: 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                                <Typography sx={{ color: '#fff', fontSize: 28, fontWeight: 700 }}>
                                    {(profile.first_name || 'F')[0]}{(profile.last_name || '')[0]}
                                </Typography>
                            </Box>
                            <Typography variant="h6" fontWeight={700}>
                                {profile.first_name} {profile.last_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">{profile.email}</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {profile.company_name}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Card>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 3 }}>
                                Edit Profile
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="First Name" value={profile.first_name} disabled />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="Last Name" value={profile.last_name} disabled />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth label="Email" value={profile.email} disabled />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth label="Company" value={profile.company_name} disabled />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth label="LinkedIn URL" value={linkedinUrl}
                                        onChange={(e) => setLinkedinUrl(e.target.value)}
                                        placeholder="https://linkedin.com/in/..."
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth label="Bio" multiline rows={4} value={bio}
                                        onChange={(e) => setBio(e.target.value)}
                                        placeholder="Tell us about yourself..."
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <LoadingButton
                                        variant="contained" loading={saving} onClick={handleSave}
                                        sx={{ bgcolor: '#1a237e', '&:hover': { bgcolor: '#0d1642' } }}
                                    >
                                        Save Changes
                                    </LoadingButton>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}
